import axios from "axios";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import {
  hasRole,
  NoUserFound,
  getOrdinalSuffix,
} from "../../../components/Utils";
import { OvalLoader } from "../../../components/common/Loader";
import MemberDetailsModal from "../../../components/common/MemberDetailsModal";
import { USER_EDIT } from "../../../routes/routes";
import {
  getTop10RankColor,
  getTop10RankTextColor,
} from "../../../constant/theme";
import userIcon from "../../../assets/images/icons/user.png";
import userIconWhite from "../../../assets/images/icons/user_white.png";
import referralsIcon from "../../../assets/images/icons/referrals.png";
import referralsIconWhite from "../../../assets/images/icons/referrals_white.png";
import tysIcon from "../../../assets/images/icons/tys.png";
import tysIconWhite from "../../../assets/images/icons/tys_white.png";
import oneToOneIcon from "../../../assets/images/icons/one_to_one.png";
import oneToOneIconWhite from "../../../assets/images/icons/one_to_one_white.png";
import visitorsIcon from "../../../assets/images/icons/visitors.png";
import visitorsIconWhite from "../../../assets/images/icons/visitors_white.png";
import chiefGuestsIcon from "../../../assets/images/icons/chief_guests.png";
import chiefGuestsIconWhite from "../../../assets/images/icons/chief_guests_white.png";
import powerDatesIcon from "../../../assets/images/icons/power_dates.png";
import powerDatesIconWhite from "../../../assets/images/icons/power_dates_white.png";
import inductionsIcon from "../../../assets/images/icons/inductions.png";
import inductionsIconWhite from "../../../assets/images/icons/inductions_white.png";

const getRankColor = getTop10RankColor;
const getRankTextColor = getTop10RankTextColor;

const ValueBadge = ({ value }) => {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "4px 12px",
        borderRadius: "16px",
        fontSize: "0.9125rem",
        fontWeight: 600,
        // background: "#ecfdf3",
        color: "#000",
        // border: "1px solid #a6f4c5",
      }}
    >
      {value}
    </span>
  );
};

const RenderTableBlock = ({
  title,
  exportPerm,
  pdfKey,
  dataList,
  valueKey,
  valueLabel,
  loading,
  hasPermission,
  pdfLoading,
  handleDownloadPDF,
  kingTitle,
  dateRangeText,
}) => {
  const top3Users = dataList?.slice(0, 3) || [];
  const [activeIndex, setActiveIndex] = useState(0);
  const [detailsModal, setDetailModal] = useState(false);
  const [detailData, setDetailData] = useState({});
  const [modalLoading, setModalLoading] = useState(false);

  const handleViewProfile = async (userId) => {
    if (!userId) return;

    setDetailModal(true);
    setModalLoading(true);
    setDetailData({});

    try {
      const res = await axios.get(USER_EDIT + userId);
      if (res.status === 200 && res.data.success) {
        setDetailData(res.data.data);
      } else {
        toast.error(res.data.message || "Failed to load member details");
        setDetailModal(false);
      }
    } catch {
      toast.error("Failed to load member details");
      setDetailModal(false);
    } finally {
      setModalLoading(false);
    }
  };

  // Swipe gesture state
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const minSwipeDistance = 40;

  // Reset active index when tab changes
  useEffect(() => {
    setActiveIndex(0);
  }, [title]);

  const handleStart = (e) => {
    setIsDragging(true);
    setTouchEnd(null);
    setTouchStart(e.type.includes("mouse") ? e.pageX : e.touches[0].clientX);
  };

  const handleMove = (e) => {
    if (!isDragging && !e.type.includes("touch")) return;
    setTouchEnd(e.type.includes("mouse") ? e.pageX : e.touches[0].clientX);
  };

  const handleEnd = () => {
    setIsDragging(false);
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) {
      // Swipe left - next card
      setActiveIndex((prev) => (prev + 1) % top3Users.length);
    } else if (distance < -minSwipeDistance) {
      // Swipe right - previous card
      setActiveIndex(
        (prev) => (prev - 1 + top3Users.length) % top3Users.length,
      );
    }
    setTouchStart(null);
    setTouchEnd(null);
  };

  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const getCardStyle = (pos) => {
    const base = {
      userSelect: "none",
      WebkitUserSelect: "none",
      MozUserSelect: "none",
      cursor: isDragging ? "grabbing" : "grab",
      margin: 0,
    };

    if (pos === 0) {
      return {
        ...base,
        position: "relative",
        zIndex: 3,
        transform: "none",
        opacity: 1,
        boxShadow: "0 20px 40px rgba(10, 30, 20, 0.5)",
        pointerEvents: "auto",
      };
    } else if (pos === 1) {
      return {
        ...base,
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 2,
        transform: "translateX(15px) translateY(6px) rotate(4deg) scale(0.95)",
        opacity: 1,
        filter: "brightness(0.85)",
        boxShadow: "0 8px 20px rgba(10, 30, 20, 0.4)",
        pointerEvents: "auto",
      };
    } else {
      return {
        ...base,
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        zIndex: 1,
        transform:
          "translateX(-15px) translateY(6px) rotate(-4deg) scale(0.95)",
        opacity: 1,
        filter: "brightness(0.85)",
        boxShadow: "0 8px 20px rgba(10, 30, 20, 0.4)",
        pointerEvents: "auto",
      };
    }
  };

  return (
    <div className="row">
      <div className="col-12 col-xl-7 mb-4 mb-xl-0">
        <div className="modern-table-wrapper">
          <div className="modern-card-header">
            <div>
              <h5 className="d-flex align-items-center mb-0">
                <i
                  className="bx bxs-trophy me-2"
                  style={{
                    color: "#8e7c13ff",
                    fontSize: "1.5rem",
                    filter: "drop-shadow(0px 2px 2px rgba(255, 215, 0, 0.4))",
                  }}
                ></i>
                {title}
              </h5>
              {dateRangeText && (
                <div
                  style={{
                    fontSize: "0.825rem",
                    color: "#6c757d",
                    fontWeight: 400,
                    marginTop: "3px",
                  }}
                >
                  {dateRangeText}
                </div>
              )}
            </div>
            {hasPermission(exportPerm) && dataList?.length > 0 && (
              <button
                className="btn-modern-download"
                title="Download PDF"
                onClick={() => {
                  if (!pdfLoading) handleDownloadPDF(pdfKey);
                }}
                disabled={pdfLoading}
              >
                <i
                  className={`bx bx-cloud-download ${pdfLoading ? "bx-fade-down" : ""}`}
                ></i>
                Download PDF
              </button>
            )}
          </div>
          <div className="rich-list-wrapper">
            {loading ? (
              <div style={{ padding: "40px" }}>
                <OvalLoader />
              </div>
            ) : dataList?.length > 0 ? (
              dataList.map((users, i) => (
                <div className="rich-list-item" key={i}>
                  <div
                    className="rank-badge"
                    style={{
                      width: "60px",
                      minWidth: "60px",
                      height: "44px",
                      background: getRankColor(i),
                      color: getRankTextColor(i),
                      fontWeight: "700",
                      fontSize: "0.95rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: "-24px",
                      marginRight: "40px",
                      paddingRight: "10px",
                      clipPath:
                        "polygon(0 0, 80% 0, 100% 50%, 80% 100%, 0 100%)",
                    }}
                  >
                    {getOrdinalSuffix(i + 1)}
                  </div>
                  <img
                    src={users?.thumb_profile_image || users?.profile_image}
                    alt="profile"
                    className="rich-item-img"
                  />
                  <div className="rich-item-info">
                    <span className="rich-item-name">{users?.name}</span>
                    <span className="rich-item-sub">
                      {users?.user_company_name ||
                        users?.company_name ||
                        "Member"}
                    </span>
                  </div>
                  {/* <div className="rich-item-location">
                    {users?.chapter_name && (
                      <span className="rich-item-loc-main">
                        {users.chapter_name}
                      </span>
                    )}
                    {users?.region_name && (
                      <span className="rich-item-loc-sub">
                        {users.region_name}
                        {users?.state_name ? `, ${users.state_name}` : ""}
                      </span>
                    )}
                    {!users?.region_name && users?.state_name && (
                      <span className="rich-item-loc-sub">
                        {users.state_name}
                      </span>
                    )}
                  </div> */}
                  <div className="rich-item-value">
                    <ValueBadge value={users?.[valueKey]} />
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: "64px 24px", textAlign: "center" }}>
                <NoUserFound
                  title={`No ${title.replace("Top 10 ", "")} Found`}
                  subtitle="Everything is on track! There are no records to display at this time."
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="col-12 col-xl-5">
        {top3Users.length > 0 && (
          <div
            className="top-performer-stack"
            style={{
              position: "relative",
              perspective: "1000px",
              touchAction: "pan-y",
              margin:
                top3Users.length === 1
                  ? "72px 16px 16px 16px"
                  : "72px auto 40px auto",
              maxWidth: top3Users.length === 1 ? "100%" : "340px",
            }}
            onTouchStart={handleStart}
            onTouchMove={handleMove}
            onTouchEnd={handleEnd}
            onMouseDown={handleStart}
            onMouseMove={handleMove}
            onMouseUp={handleEnd}
            onMouseLeave={handleEnd}
          >
            {top3Users.map((topUser, index) => {
              const pos =
                (index - activeIndex + top3Users.length) % top3Users.length;
              return (
                <div
                  key={index}
                  className="top-performer-card"
                  onClick={() => setActiveIndex(index)}
                  style={{
                    ...getCardStyle(pos),
                    transformOrigin: "bottom center",
                    transition: "all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)",
                  }}
                >
                  <div className="top-performer-header">
                    {index === 0 && (
                      <span className="badge-star">
                        <i
                          className="bx bxs-crown"
                          style={{ color: "#FFD700", marginRight: "4px" }}
                        ></i>
                        {kingTitle || "Star Performer"}
                      </span>
                    )}
                  </div>

                  <div className="top-performer-content">
                    <div className="watermark-bg">{getOrdinal(index + 1)}</div>

                    <div className="performer-layout">
                      <div className="performer-stats">
                        <div className="p-stat">
                          <span className="p-stat-label">
                            {valueLabel.toUpperCase()}
                          </span>
                          <span className="p-stat-val">
                            {topUser[valueKey] || 0}
                          </span>
                        </div>
                        <div className="p-stat p-stat-sm">
                          <span className="p-stat-label">ROLE</span>
                          <span className="p-stat-val-small">
                            {topUser.user_role_name ||
                              topUser.role_name ||
                              "N/A"}
                          </span>
                        </div>
                        <div className="p-stat p-stat-sm">
                          <span className="p-stat-label">CONTACT</span>
                          <span className="p-stat-val-small">
                            {topUser.mobile_number || "N/A"}
                          </span>
                        </div>
                        <div className="p-stat p-stat-sm">
                          <span className="p-stat-label">CATEGORY</span>
                          <span className="p-stat-val-small">
                            {topUser.category_name || "N/A"}
                          </span>
                        </div>
                        <div className="p-stat p-stat-sm">
                          <span className="p-stat-label">COMPANY</span>
                          <span className="p-stat-val-small">
                            {topUser.user_company_name ||
                              topUser.company_name ||
                              "N/A"}
                          </span>
                        </div>
                      </div>

                      <div className="performer-image-container">
                        <div className="performer-img-wrapper">
                          <img
                            src={
                              topUser.profile_image ||
                              topUser.from_profile_image ||
                              topUser.user_profile_image ||
                              "https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=256&q=80"
                            }
                            alt="performer"
                            className="performer-img"
                          />
                        </div>
                        <div className="performer-glow"></div>
                      </div>
                    </div>
                  </div>

                  <div className="top-performer-footer">
                    <div className="performer-info">
                      <h4 className="performer-name">
                        {topUser.name ||
                          topUser.from_user_name ||
                          topUser.user_name}
                      </h4>
                      <p className="performer-chapter">
                        {topUser.chapter_name || "N/A"},{" "}
                        {topUser.region_name || "N/A"}
                      </p>
                    </div>
                    <button
                      className="btn-view-profile"
                      style={{ visibility: pos === 0 ? "visible" : "hidden" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewProfile(
                          topUser.id || topUser.from_user_id || topUser.user_id,
                        );
                      }}
                    >
                      View Profile
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <MemberDetailsModal
        show={detailsModal}
        onHide={() => setDetailModal(false)}
        detailData={detailData}
        loading={modalLoading}
      />
    </div>
  );
};

const TopTenTables = ({
  data,
  loading,
  hasPermission,
  pdfLoading,
  handleDownloadPDF,
  dateRangeText,
}) => {
  const tabs = [
    {
      id: "points",
      label: "Points",
      imageIcon: userIcon,
      imageIconWhite: userIconWhite,
      perm: "dashboard-points-data-view",
    },
    {
      id: "rfls",
      label: "RFLs Given",
      imageIcon: referralsIcon,
      imageIconWhite: referralsIconWhite,
      perm: "dashboard-rfls-data-view",
    },
    {
      id: "tyns",
      label: "Business Given",
      imageIcon: tysIcon,
      imageIconWhite: tysIconWhite,
      perm: "dashboard-tyns-data-view",
    },
    {
      id: "121s",
      label: "R2R",
      imageIcon: oneToOneIcon,
      imageIconWhite: oneToOneIconWhite,
      perm: "dashboard-121s-data-view",
    },
    {
      id: "visitors",
      label: "Visitors Invited",
      imageIcon: visitorsIcon,
      imageIconWhite: visitorsIconWhite,
      perm: "dashboard-visitors-data-view",
    },
    {
      id: "chief_guests",
      label: "Chief Guests",
      imageIcon: chiefGuestsIcon,
      imageIconWhite: chiefGuestsIconWhite,
      perm: "dashboard-visitors-data-view",
    },
    {
      id: "inductions",
      label: "Inductions",
      imageIcon: inductionsIcon,
      imageIconWhite: inductionsIconWhite,
      perm: "dashboard-visitors-data-view", // Existing perm used for Inductions
    },
    {
      id: "power_dates",
      label: "Power Dates",
      imageIcon: powerDatesIcon,
      imageIconWhite: powerDatesIconWhite,
      perm: "dashboard-visitors-data-view",
    },
  ];

  const tabConfig = {
    points: {
      title: "Top 10 Performing Members",
      exportPerm: "dashboard-points-data-view-export",
      pdfKey: "top_10_points_members",
      dataList: data.top10PointsUsersList,
      valueKey: "points",
      valueLabel: "Points",
      kingTitle: "King of Performers",
    },
    tyns: {
      title: "Top 10 Business Given Members",
      exportPerm: "dashboard-tyns-data-view-export",
      pdfKey: "top_10_business_given_members",
      dataList: data.top10BusinessUsersList,
      valueKey: "business_value",
      valueLabel: "Amount",
      kingTitle: "King of TYNs",
    },
    rfls: {
      title: "Top 10 RFLs Given Members",
      exportPerm: "dashboard-rfls-data-view-export",
      pdfKey: "top_10_referral_given_members",
      dataList: data.top10ReferralsGivenUsersList,
      valueKey: "referrals_count",
      valueLabel: "Count",
      kingTitle: "King of RFLs",
    },
    visitors: {
      title: "Top 10 Visitors Invited Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_visitors_members",
      dataList: data.top10VisitorsGivenUsersList,
      valueKey: "visitors_count",
      valueLabel: "Count",
      kingTitle: "King of Visitors",
    },
    "121s": {
      title: "Top 10 R2R By Members",
      exportPerm: "dashboard-121s-data-view-export",
      pdfKey: "top_10_121_members",
      dataList: data.top10121sGivenUsersList,
      valueKey: "one2one_count",
      valueLabel: "Count",
      kingTitle: "King of R2R",
    },
    inductions: {
      title: "Top 10 Inductions Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_inductions_members",
      dataList: data.top10InductionsGivenUsersList,
      valueKey: "inductions_count",
      valueLabel: "Count",
      kingTitle: "King of Inductions",
    },
    power_dates: {
      title: "Top 10 Power Dates Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_power_dates_members",
      dataList: data.top10PowerDateGivenUsersList,
      valueKey: "power_dates_count",
      valueLabel: "Count",
      kingTitle: "King of Power Dates",
    },
    chief_guests: {
      title: "Top 10 Chief Guests Members",
      exportPerm: "dashboard-visitors-data-view-export",
      pdfKey: "top_10_chief_guests_members",
      dataList: data.top10ChiefGuestGivenUsersList,
      valueKey: "chief_guests_count",
      valueLabel: "Count",
      kingTitle: "King of Chief Guests",
    },
  };

  const allowedTabs = tabs.filter((tab) => hasPermission(tab.perm));
  const [activeTab, setActiveTab] = useState(
    allowedTabs.length > 0 ? allowedTabs[0].id : "",
  );

  useEffect(() => {
    if (
      allowedTabs.length > 0 &&
      !allowedTabs.find((t) => t.id === activeTab)
    ) {
      setActiveTab(allowedTabs[0].id);
    }
  }, [hasPermission]);

  const tabsContainerRef = useRef(null);
  const scrollCheckRafRef = useRef(null);
  const [hasTabScroll, setHasTabScroll] = useState(false);

  const evaluateTabScroll = useCallback(() => {
    const container = tabsContainerRef.current;
    if (!container) return;

    const tabElements = container.querySelectorAll(".modern-tab");
    if (!tabElements.length) {
      setHasTabScroll(false);
      return;
    }

    const containerStyles = window.getComputedStyle(container);
    const gap =
      parseFloat(containerStyles.columnGap || containerStyles.gap || "0") || 0;
    const horizontalPadding =
      parseFloat(containerStyles.paddingLeft) +
      parseFloat(containerStyles.paddingRight);

    let contentWidth = 0;
    tabElements.forEach((tab) => {
      contentWidth += tab.scrollWidth;
    });

    const requiredWidth =
      contentWidth + gap * Math.max(tabElements.length - 1, 0) + horizontalPadding;
    const availableWidth = container.clientWidth;
    const hasClippedTab = Array.from(tabElements).some(
      (tab) => tab.scrollWidth > tab.clientWidth + 1,
    );

    setHasTabScroll((prev) => {
      if (prev) {
        return requiredWidth > availableWidth - 8;
      }
      return hasClippedTab;
    });
  }, []);

  const scheduleTabScrollCheck = useCallback(() => {
    if (scrollCheckRafRef.current) {
      cancelAnimationFrame(scrollCheckRafRef.current);
    }

    scrollCheckRafRef.current = requestAnimationFrame(() => {
      evaluateTabScroll();
      scrollCheckRafRef.current = null;
    });
  }, [evaluateTabScroll]);

  useEffect(() => {
    scheduleTabScrollCheck();

    const container = tabsContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(scheduleTabScrollCheck);
    resizeObserver.observe(container);

    window.addEventListener("resize", scheduleTabScrollCheck);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", scheduleTabScrollCheck);
      if (scrollCheckRafRef.current) {
        cancelAnimationFrame(scrollCheckRafRef.current);
      }
    };
  }, [allowedTabs.length, activeTab, scheduleTabScrollCheck]);

  if (allowedTabs.length === 0) return null;

  const config = tabConfig[activeTab];

  return (
    <>
      <div className="row dash-table mb-5 pb-5">
        <div className="col-12">
          <div className="card modern-card mb-4">
            <div
              ref={tabsContainerRef}
              className={`modern-tabs-container${hasTabScroll ? " has-tab-scroll" : ""
                }`}
            >
              {allowedTabs.map((tab) => (
                <div
                  key={tab.id}
                  className={`modern-tab ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  <img
                    src={
                      activeTab === tab.id
                        ? tab.imageIcon
                        : tab.imageIconWhite
                    }
                    alt={tab.label}
                    className="modern-tab-icon"
                  />
                  <span>{tab.label}</span>
                </div>
              ))}
            </div>

            {config && (
              <RenderTableBlock
                {...config}
                loading={loading}
                hasPermission={hasPermission}
                pdfLoading={pdfLoading}
                handleDownloadPDF={handleDownloadPDF}
                dateRangeText={dateRangeText}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TopTenTables;
